import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '../overlay/Dialog';
import apolloClient from '../../services/apolloClient';
import {Tabs, Tab} from 'material-ui/Tabs';
import queries from '../../graphql/queries';
import mutations from '../../graphql/mutations';
import OverlayController from '../overlay/OverlayController';
import CircularProgress from 'material-ui/CircularProgress';
import PluginsManager from '../plugins/PluginsManager';
import {itemPassesFilter} from "../../services/itemFilterService";
import { compose } from 'react-apollo';
import { GraphQLStackTrace } from '../../utils/utils';
import _ from 'lodash';
import './ItemDialog.css'


export default compose(
    queries.projectQueryHocWithName('projectData'),
    queries.itemQueryHocWithName('itemData'),
    queries.itemLogsQueryHoc,
    mutations.updateItemMutationHoc,
    mutations.reportEventMutationHoc,
    mutations.assignCurrentUserMutationHoc,
    mutations.executeActionMutationHoc
)(class ItemDialog extends React.Component {

    static propTypes = {
        projectId: PropTypes.string.isRequired,
        itemId: PropTypes.string.isRequired,
        queueItemViewButtons: PropTypes.array.isRequired,
        projectData: PropTypes.object.isRequired, // injected by queries.projectQueryHocWithName('projectData')
        itemData: PropTypes.object.isRequired, // injected by queries.projectQueryHocWithName('projectData')
        logsData: PropTypes.object.isRequired, // injected by queries.itemLogsQueryHoc
        updateItem: PropTypes.func,
        reportEvent: PropTypes.func,
        assignCurrentUser: PropTypes.func,
        executeAction: PropTypes.func
    };

    static defaultProps = {

    };

    constructor(props) {
        super(props);

        this.tabs = [];
        this.currentTabElement = null;

        this.state = this.stateFromProps(props);
    }

    componentWillReceiveProps(nextProps) {
        this.setState(this.stateFromProps(nextProps));
    }

    stateFromProps(props) {
        return {
            plugins: this.filteredPluginsFromProps(props),
            actions: this.filteredActionButtonTransitionsFromProps(props)
        };
    }

    filteredPluginsFromProps(props) {
        if (!props.projectData.project) {
            return null;
        }
        return props.projectData.project.plugins.filter(plugin => {
            if (!('PluginItemView' in PluginsManager.getPluginByTypeId(plugin.typeId))) {
                return false;
            }
            if (plugin.useForAllStates) {
                return true;
            }
            return props.itemData.item.states.some(state => plugin.stateIds.includes(state.id));
        });
    }

    filteredActionButtonTransitionsFromProps(props) {
        let project = props.projectData.project;
        let itemStateIds = props.itemData.item.states.map(state => state.id);
        let statesById = _.keyBy(project.states, 'id');
        let itemFlowIds = itemStateIds.map(itemStateId => statesById[itemStateId].flowId);

        // Find transitions eligible for the current item
        let transitions = _.filter(project.transitions, transition => {
            let stateFrom = statesById[transition.stateFromId];
            let stateTo = statesById[transition.stateToId];

            // If transition's source state is "null", then pass this transition only if its target state is within
            // a flow in which the item does not have any a state, otherwise filter out this transition.
            if (stateFrom.type === 'null') {
                return !itemFlowIds.includes(stateTo.flowId);
            }

            // If transition's source state is "fromAny", then pass this transition only if its target state is located
            // in a flow in which the item already has a state, but is not the same as the target state,
            // otherwise filter out this transition.
            if (stateFrom.type === 'fromAny') {
                return itemFlowIds.includes(stateTo.flowId) && !itemStateIds.includes(transition.stateToId);
            }

            return itemStateIds.includes(transition.stateFromId);
        });

        let transitionEventIds = transitions.map(transition => transition.transitionEventId);

        // Find actions eligible for the current item.
        return _.filter(project.actions, action => {

            // If an action has a transitionEventId, then pass it only there is a eligible transition
            if (action.transitionEventId && !transitionEventIds.includes(action.transitionEventId)) {
                return false;
            }

            // Check if item passes the action filter.
            return itemPassesFilter(props.itemData.item, action.itemFilter, project.states);
        });
    }

    onActionButtonClicked(button) {
        OverlayController.showActivityIndicator();
        let promises = [];
        if (button.eventName) {
            promises.push(this.props.reportEvent(this.props.projectId, this.props.itemData.item, button.eventName));
        } else if (button.flowIdForUserAssignment) {
            promises.push(this.props.assignCurrentUser(this.props.projectId, this.props.itemData.item, button.flowIdForUserAssignment));
        } else if (button.action) {
            promises.push(this.props.executeAction(this.props.projectId, this.props.itemData.item, button.action.id));
        }
        Promise.all(promises)
            .then(() => {
                OverlayController.hideActivityIndicator();
                OverlayController.hideOverlay();
            })
            .catch((error) => {
                OverlayController.hideActivityIndicator();
                OverlayController.showErrorAlert(error);
            });
    }

    onTabChange(tab) {
        let tabElement;
        switch (tab) {
            case "data":
                tabElement = this.dataTab;
                break;
            case "log":
                tabElement = this.logTab;
                break;
            default:
                tabElement = this.tabs[tab];
                break;
        }
        this.currentTabElement.style.display = 'none';
        this.currentTabElement = tabElement;
        this.currentTabElement.style.display = 'flex';
    }

    onPluginItemUpdate(options = {}, callback) {
        let promise = this.props.updateItem(this.props.projectId, this.props.itemData.item, options.data, options.tags);
        if (callback && typeof callback === "function") {
            promise.then(item => {
                callback(null, item)
            }).catch(error => {
                callback(error, null)
            });
        } else {
            return promise;
        }
    }

    onPluginReportEvent(eventName, callback) {
        let promise = this.props.reportEvent(this.props.projectId, this.props.itemData.item, eventName);
        if (callback && typeof callback === "function") {
            promise.then(item => {
                callback(null, item)
            }).catch(error => {
                callback(error, null)
            });
        } else {
            return promise;
        }
    }

    onPluginRefreshQueues(callback) {
        let promise = apolloClient.queryManager.refetchQueryByName('itemListQuery').then((result) => {});
        if (callback && typeof callback === "function") {
            promise.then(() => {
                callback(null)
            }).catch(error => {
                callback(error)
            });
        } else {
            return promise;
        }
    }

    componentDidMount() {
        this.updateTabsIfNeeded();
    }

    componentDidUpdate() {
        this.updateTabsIfNeeded();
    }

    updateTabsIfNeeded() {
        if ((this.tabs.length || this.dataTab) && !this.currentTabElement) {
            this.currentTabElement = this.tabs.length ? this.tabs[0] : this.dataTab;
            this.currentTabElement.style.display = 'flex';
        }
    }

    getActionButtons() {
        let actions = [{label: 'Close', handler: () => OverlayController.hideOverlay()}];
        this.props.queueItemViewButtons.forEach(actionButton => {
            actions.push({
                label: actionButton.title,
                primary: true,
                handler: () => this.onActionButtonClicked(actionButton)
            });
        });
        this.state.actions.forEach(action => {
            actions.push({
                label: action.title,
                primary: true,
                handler: () => this.onActionButtonClicked({
                    action: action
                })
            });
        });
        return actions;
    }

    getPluginItemViewByPlugin(projectPlugin) {
        let plugin = PluginsManager.getPluginByTypeId(projectPlugin.typeId);
        let PluginItemView = plugin.PluginItemView;
        return <PluginItemView
            configuration={projectPlugin.configuration}
            item={this.props.itemData.item}
            projectId={this.props.projectId}
            updateItem={this.onPluginItemUpdate.bind(this)}
            reportEvent={this.onPluginReportEvent.bind(this)}
            refreshQueues={this.onPluginRefreshQueues.bind(this)}
        />
    }

    render() {
        if (this.props.projectData.loading) {
            return <CircularProgress className="activityIndicator"/>;
        } else if (this.props.projectData.error) {
            return <pre>{GraphQLStackTrace(this.props.projectData.error)}</pre>;
        }
        return (
            <Dialog showTitleBar={true} title={`Item`} actions={this.getActionButtons()} className="item-dialog">
                <Tabs
                    inkBarStyle={{height: 4, marginTop: -4}}
                    className="item-dialog-tabs"
                    contentContainerClassName="item-dialog-tabs-content-container"
                    tabItemContainerStyle={{flexShrink: 0}}
                    onChange={this.onTabChange.bind(this)}
                >
                    {this.state.plugins.map((plugin, index) => (
                        <Tab label={plugin.title} value={index} key={index}>
                            <div className="item-dialog-content-wrapper" ref={(el) => {this.tabs[index] = el ? el.parentElement : null;}}>
                                {this.getPluginItemViewByPlugin(plugin)}
                            </div>
                        </Tab>
                    ))}
                    <Tab label="Data" value="data">
                        <div className="item-dialog-content-wrapper" ref={(el) => {this.dataTab = el ? el.parentElement : null;}}>
                            <div className="item-dialog-item-info">
                                <div>id: <b>{this.props.itemData.item.id}</b></div>
                                <div>clientId: <b>{this.props.itemData.item.clientId || "null"}</b></div>
                                <div>created: <b>{new Date(this.props.itemData.item.createdAt).toLocaleString()}</b></div>
                                <div>updated: <b>{new Date(this.props.itemData.item.updatedAt).toLocaleString()}</b></div>
                                <div>tags: <b>[{this.props.itemData.item.tags.join(', ')}]</b></div>
                                <div>states: <b>[{this.props.itemData.item.states.map(state => state.name).join(', ')}]</b></div>
                                <div>assignedUsers: <b>[{this.props.itemData.item.assignedUsers.map(assignedUser => '{user:' + assignedUser.user.email + ', flow:' + assignedUser.flow.name + '}').join(', ')}]</b></div>
                                <div>data:<br/><pre>{JSON.stringify(this.props.itemData.item.data, null, 4)}</pre></div>
                            </div>
                        </div>
                    </Tab>
                    <Tab label="Log" value="log">
                        <div className="item-dialog-content-wrapper" ref={(el) => {this.logTab = el ? el.parentElement : null;}}>
                            {this.props.logsData.loading ? (
                                <CircularProgress className="activityIndicator"/>
                            ) : this.props.logsData.error ? (
                                <pre>{GraphQLStackTrace(this.props.logsData.error)}</pre>
                            ) : (
                                <ul className="item-dialog-log-list">
                                    {this.props.logsData.item.logs.map(log =>
                                        <li key={log.id} className="item-dialog-log-list-item">
                                            <div className="item-dialog-log-list-message"><span
                                                className="item-dialog-log-list-message-date">{new Date(log.createdAt).toISOString()}</span>: {log.message}
                                            </div>
                                        </li>
                                    )}
                                </ul>
                            )}
                        </div>
                    </Tab>
                </Tabs>
            </Dialog>
        );
    }
});



// WEBPACK FOOTER //
// ./src/components/item/ItemDialog.js